package com.hyperether.goodjob.scenes.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import com.hyperether.goodjob.theme.BlueApp
import com.hyperether.goodjob.theme.FooterText
import com.hyperether.goodjob.theme.UnselectedPage
import com.hyperether.goodjob.util.toScaleDp
import com.hyperether.goodjob.util.toScaleSp


@Composable
fun <T> Pagination(
    list: List<T>,
    onPageChange: (Int) -> Unit,
    currentPage: Int,
    itemsPerPage: Int = 6
) {
    val totalPages =
        (list.size + itemsPerPage - 1) / itemsPerPage // Calculate total pages

    // Pagination Logic Adjustment for Small Lists
    val visiblePages = mutableListOf<Int>()
    if (totalPages <= 3) {
        for (page in 1..totalPages) visiblePages.add(page) // Show 1, 1 2, or 1 2 3
    } else {
        // Use the dynamic logic for larger lists
        when {
            // Start (first few pages)
            currentPage <= 3 -> {
                visiblePages.addAll(1..3)
                visiblePages.add(-1) // Ellipsis at 4th place
                visiblePages.add(totalPages)
            }

            // Middle Pages
            currentPage in 4..(totalPages - 3) -> {
                visiblePages.add(currentPage - 1)
                visiblePages.add(currentPage)
                visiblePages.add(currentPage + 1)
                visiblePages.add(-1) // Ellipsis at 4th place
                visiblePages.add(totalPages)
            }

            // Near the End
            else -> {
                visiblePages.add(1)
                visiblePages.add(-1) // Ellipsis at 2nd place
                visiblePages.addAll((totalPages - 2)..totalPages)
            }
        }
    }

    val startIndex = (currentPage - 1) * itemsPerPage + 1
    val endIndex = (currentPage * itemsPerPage).coerceAtMost(list.size)


    // Render Pagination
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 17.toScaleDp(), bottom = 32.toScaleDp(), start = 40.toScaleDp(), end = 40.toScaleDp()),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text(
            fontSize = 14.toScaleSp(),
            text = "$startIndex - $endIndex of ${list.size}",
            color = FooterText,
            modifier = Modifier.align(Alignment.CenterVertically),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )

        Row(
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Previous Page Button
            Box(
                modifier = Modifier
                    .size(24.toScaleDp())
                    .clip(RoundedCornerShape(4.toScaleDp()))
                    .background(UnselectedPage)
                    .clickable(enabled = currentPage > 1) { onPageChange(currentPage - 1) }
                    .pointerHoverIcon(PointerIcon.Hand),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = "<",
                    fontSize = 12.toScaleSp(),
                    lineHeight = 14.toScaleSp(),
                    color = Color.Gray,
                    fontWeight = FontWeight.W500,
                    textAlign = TextAlign.Center
                )
            }

            Spacer(modifier = Modifier.width(12.toScaleDp()))

            visiblePages.distinct().forEach { page ->
                if (page == -1) {
                    Text(
                        modifier = Modifier
                            .size(24.toScaleDp())
                            .clip(RoundedCornerShape(4.toScaleDp())),
                        text = "...",
                        fontSize = 12.toScaleSp(),
                        lineHeight = 14.toScaleSp(),
                        color = Color.Gray,
                        textAlign = TextAlign.Center
                    )
                } else {
                    val isSelected = currentPage == page
                    Box(
                        modifier = Modifier
                            .size(24.toScaleDp())
                            .clip(RoundedCornerShape(4.toScaleDp()))
                            .background(if (isSelected) BlueApp else UnselectedPage)
                            .clickableWithPointer { onPageChange(page) },
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = page.toString(),
                            fontSize = 12.toScaleSp(),
                            color = if (isSelected) Color.White else Color.Black,
                            fontWeight = FontWeight.W500,
                            lineHeight = 14.toScaleSp(),
                            textAlign = TextAlign.Center
                        )
                    }
                }
                Spacer(modifier = Modifier.width(12.toScaleDp()))
            }

            // Next Page Button
            Box(
                modifier = Modifier
                    .size(24.toScaleDp())
                    .clip(RoundedCornerShape(4.toScaleDp()))
                    .background(UnselectedPage)
                    .clickable(enabled = currentPage < totalPages) {
                        onPageChange(currentPage + 1)
                    }
                    .pointerHoverIcon(PointerIcon.Hand),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = ">",
                    fontSize = 12.toScaleSp(),
                    color = Color.Gray,
                    fontWeight = FontWeight.W500,
                    lineHeight = 14.toScaleSp(),
                    textAlign = TextAlign.Center
                )
            }
        }
    }
}
